// R-131-FIN-DECISION-A — gas simple-cycle peaker pathway preset (EIA AEO).

import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import { DEFAULT_GAS_FUEL_PRICE_CAGR_PCT } from '../../inflationBasis';
import type { Pathway } from '../types';
import { baseloadEnergyCfPct } from '../../phaseFeasibility';
import {
  DEFAULT_BASE_WACC_PCT,
  annualMwhFromCf,
  buildCapexBlock,
  buildStandardBtmPhases,
  capexFromPerKw,
  computeAnnualOpexUsd,
  mergePathway,
  pathwayIdFromTechnology,
  resolveFacilityLoadMw,
  resolveNameplateMw,
  resolveProjectStartDate,
  valueOfServedLoadRevenue,
  type PathwayPresetOptions,
} from './shared';

/** Peaker-class aeroderivative / F-class simple cycle — higher heat rate, lower CF. */
export const GAS_SC_BENCHMARKS = {
  capexPerKwUsd: 850,
  fixedOmPerKwYearUsd: 18,
  variableOmPerMwhUsd: 5,
  /** $5/MMBtu × 10,500 Btu/kWh heat rate. */
  fuelCostPerMwhUsd: 52.5,
  designLifeYears: 25,
  availabilityPct: 85,
  /**
   * MARKET / merchant-dispatch capacity factor (EIA AEO peaker class, ~15%).
   * This is the SOURCED figure and intentionally STAYS as-is — it is the right
   * CF for a peaker selling into a wholesale market.
   *
   * It is NOT used to compute the LOAD-SERVING annual energy below. A reciprocating
   * engine dedicated to a firm 24/7 datacenter runs BASELOAD, not market dispatch,
   * so its delivered energy is driven by the baseload operating CF
   * (`baseloadEnergyCfPct('recip')`, ≈92%), consistent with how the nameplate was
   * sized (firm/energy tiers at the capacity credit). See `presetFor` below.
   * (R-131-FIN-DECISION-A modeling choice; RULE 5.)
   */
  capacityFactorPct: 15,
  constructionMonths: 18,
  riskPremiumBps: 75,
  fuelPriceCagrPct: DEFAULT_GAS_FUEL_PRICE_CAGR_PCT,
  fuelPriceStdev: 5,
  tCo2ePerMwh: 0.55,
  decommPctOfCapex: 0.05,
  contingencyPct: 10,
} as const;

export function presetFor(
  intent: ProjectIntent,
  _siteContext: SiteContext,
  options?: PathwayPresetOptions,
): Pathway {
  // Generation NAMEPLATE: facility LOAD grossed up to a plant that SERVES the
  // firm 24/7 load (gas-sc dispatchable ≈ load × 1.25). Capex / energy / O&M
  // ride this; the load is carried separately for load-tracking costs.
  const facilityLoadMw = resolveFacilityLoadMw(intent);
  const capacityMw = resolveNameplateMw(intent, 'gas-sc');
  const startDate = resolveProjectStartDate(intent);
  const b = GAS_SC_BENCHMARKS;

  const baseCapexUsd = capexFromPerKw(capacityMw, b.capexPerKwUsd);
  const decommissioningUsd = baseCapexUsd * b.decommPctOfCapex;
  // Annual / delivered energy rides the BASELOAD operating CF, NOT the market
  // peaker CF (`b.capacityFactorPct` = 15%). A reciprocating engine dedicated to
  // a firm 24/7 datacenter runs baseload (≈92% of the time, = its capacity
  // credit), consistent with how the NAMEPLATE was sized above
  // (resolveNameplateMw → energy + firm tiers on the credit). Using the 15%
  // market CF here would report only ~164k MWh against a ~876k MWh 24/7 load and
  // blow up LCOE; the baseload CF lands delivered energy ≥ the load energy.
  // (R-131-FIN-DECISION-A modeling choice; RULE 5. Market CF preserved on the
  // benchmark for merchant-dispatch use.)
  const baseloadCfPct = baseloadEnergyCfPct('recip');
  const annualMwh = annualMwhFromCf(capacityMw, baseloadCfPct);
  const annualOpexUsd = computeAnnualOpexUsd(
    capacityMw,
    b.fixedOmPerKwYearUsd,
    b.variableOmPerMwhUsd,
    b.fuelCostPerMwhUsd,
    annualMwh,
  );

  const capex = buildCapexBlock(startDate, b.constructionMonths, baseCapexUsd, {
    contingencyPct: b.contingencyPct,
  });

  const codYear =
    new Date(startDate).getUTCFullYear() + Math.ceil(b.constructionMonths / 12);

  const base: Pathway = {
    id: pathwayIdFromTechnology('gas-sc'),
    name: 'BTM Gas Simple Cycle (Peaker)',
    classification: 'btm',
    technology: 'gas-sc',
    capacityMW: capacityMw,
    facilityLoadMW: facilityLoadMw,
    phases: buildStandardBtmPhases({
      startDate,
      constructionMonths: b.constructionMonths,
      designLifeYears: b.designLifeYears,
      totalCapexUsd: baseCapexUsd,
      decommissioningUsd,
      annualMwh,
      annualOpexUsd,
    }),
    capex,
    opex: {
      fixedOmPerKwYearUsd: b.fixedOmPerKwYearUsd,
      variableOmPerMwhUsd: b.variableOmPerMwhUsd,
      fuelCostPerMwhUsd: b.fuelCostPerMwhUsd,
      fuelPriceCagrPct: b.fuelPriceCagrPct,
      fuelPriceStdev: b.fuelPriceStdev,
    },
    // VALUE-CONSISTENT credit — same value-of-served-load as every other
    // pathway (incl. grid). See valueOfServedLoadRevenue in shared.ts.
    revenue: valueOfServedLoadRevenue({
      termYears: b.designLifeYears,
      availabilityPct: b.availabilityPct,
    }),
    risk: {
      technologyMaturity: 'mature',
      regulatoryRisk: 'low',
      fuelPriceRisk: 'high',
      baseWaccPct: DEFAULT_BASE_WACC_PCT,
      riskPremiumBps: b.riskPremiumBps,
    },
    carbon: {
      tCo2ePerMwh: b.tCo2ePerMwh,
    },
    lifetime: {
      designLifeYears: b.designLifeYears,
      decommissioningUsd,
      decommissioningYear: codYear + b.designLifeYears,
    },
  };

  return mergePathway(base, options?.overrides);
}
